import React from "react";
import { useNavigate } from "react-router-dom";

import PowerGeneration from "../../../assets/power-generation.webp";
import { pageConfigBySlug } from "../../../services/nav/NavigationService";
import {
  IDashboardSolarPayload,
  IDashboardTileResult,
} from "../../../services/zeos-api/contracts/Dashboard";
import { dashboardUnitOr } from "../../../services/helpers/DashboardTileHelpers";
import { DashboardMetricTile } from "../shared/DashboardMetricTile";

// A vendor outage month filled from last year's production (API DerapiGapEstimator). The tile's
// Partial message says the same, but a tile with usable figures shows the figures, not the message.
const ESTIMATED_PRODUCTION_ALERT =
  "Includes estimated months: the solar vendor reported no data, so they use last year's production, scaled.";

interface IProps {
  setUnlocked: (unlocked: boolean) => void;
  unlocked: boolean;
  tileResult?: IDashboardTileResult<IDashboardSolarPayload>;
  loading?: boolean;
}

export const SolarArray = (props: IProps) => {
  const navigate = useNavigate();
  const exploreSlug = pageConfigBySlug("explore")?.slug || "explore";
  const data = props.tileResult?.data;

  return (
    <DashboardMetricTile
      tileKey="solar"
      title="Power Generation"
      image={PowerGeneration}
      navPath={pageConfigBySlug("solar")?.slug || "solar"}
      unlocked={props.unlocked}
      setUnlocked={props.setUnlocked}
      tileResult={props.tileResult}
      loading={props.loading}
      lockedCopy={{
        bodyHeading: "HOW MUCH COULD YOU SAVE BY GOING SOLAR?",
        bodyContent:
          "See real-time information on what you generate and your energy costs.",
        onExploreClick: () =>
          navigate(`/${exploreSlug}/power-generation/intent`),
      }}
      metrics={[
        {
          label: "Bill Credits Earned",
          value: data?.billCredits,
          unit: dashboardUnitOr(data?.billCreditsUnit, "$"),
          tone: "neutral",
          alert: data?.billCreditsIsEstimated
            ? "Estimated: uses a default $0.12/kWh rate."
            : data?.billCreditsRateIsCircular
              ? // The rate is the bill total / electric kWh, and UtilityAPI's total includes gas delivery, so this is not
                // a lower bound. Real credits come only from National Grid's remote credit lines (4053, Ken 10/15).
                "Estimated from this property's average bill rate (includes gas); not the actual National Grid credit."
              : undefined,
        },
        {
          label: "Generated",
          value: data?.energyProduction,
          unit: dashboardUnitOr(data?.energyProductionUnit, "kWh"),
          tone: "positive",
          alert: data?.productionIsEstimated ? ESTIMATED_PRODUCTION_ALERT : undefined,
        },
      ]}
    />
  );
};
